"use client";

import { POSTS_API } from "@/lib/apis";
import CustomEditorProvider from "@/providers/custom-editor-provider";
import { IGetPostCommentsResponseDTO, TCommunityFeedPost } from "@/types/post";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Loader2Icon } from "lucide-react";
import { useMemo, useState } from "react";
import PostCommentCard from "./comment-card";
import PostCommentInputField from "./comment-input-field";

type TReplyToComment = {
	show: boolean;
	commentId: string | undefined;
};

const fetchComments = async ({
	postSlug,
	pageParam = 1,
	perPageParam = 10,
}: {
	postSlug: string;
	pageParam: number;
	perPageParam?: number;
}) => {
	const url = new URL(`${POSTS_API}/${postSlug}/comments`);
	url.searchParams.set("page", pageParam.toString());
	url.searchParams.set("perPage", perPageParam.toString());

	const response = await fetch(url);
	const data = await response.json();

	return data;
};

const PostComments = ({ post }: { post: TCommunityFeedPost }) => {
	const [replyToComment, setShowReplyToComment] = useState<TReplyToComment>();

	const { hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, error, isError, data } =
		useInfiniteQuery<IGetPostCommentsResponseDTO>({
			queryKey: ["post-comments", post.slug],
			queryFn: ({ pageParam }) =>
				fetchComments({
					postSlug: post.slug,
					pageParam,
					perPageParam: 10,
				}),
			getNextPageParam: (lastPage) =>
				lastPage.pagination?.page ? lastPage.pagination?.page + 1 : undefined,
		});

	const mergedComments = useMemo(() => {
		const formattedComments: IGetPostCommentsResponseDTO["data"]["comments"] = [];

		data?.pages?.forEach((page) => {
			if (page?.data) {
				page?.data?.comments.forEach((comment) => formattedComments.push(comment));
			}
		});

		return formattedComments;
	}, [data?.pages]);

	const toggleReply = (commentId?: string) => {
		if (!replyToComment?.show && commentId) {
			setShowReplyToComment({ show: true, commentId });
		} else {
			setShowReplyToComment(undefined);
		}
	};

	return (
		<>
			<CustomEditorProvider defaultValue={{ editorType: "comment" }}>
				<PostCommentInputField postSlug={post.slug} />
			</CustomEditorProvider>

			<div className="mt-6 grid gap-4">
				{isLoading ? (
					<Loader2Icon className="mx-auto h-8 w-8 animate-spin text-brand-primary" />
				) : isError ? (
					<span>{error as unknown as string}</span>
				) : mergedComments.length > 0 ? (
					mergedComments?.map((comment, i) => (
						<CustomEditorProvider key={i} defaultValue={{ editorType: "comment" }}>
							<PostCommentCard
								isReply={false}
								key={comment.id}
								comment={comment}
								postSlug={post.slug}
								showReplyInput={replyToComment?.show}
								selectedCommentId={replyToComment?.commentId}
								toggleReply={toggleReply}
							/>
						</CustomEditorProvider>
					))
				) : null}
			</div>

			<div className="mt-6">
				{!isLoading && !hasNextPage && mergedComments.length ? (
					<div className="flex items-center justify-center gap-x-1 text-sm font-medium text-gray-400">
						<span>You have reached the end.</span>
					</div>
				) : !isLoading && hasNextPage ? (
					<button
						onClick={() => fetchNextPage()}
						className="mx-auto block max-h-14 w-fit rounded-lg bg-white px-6 py-3 text-center text-sm font-medium text-gray-900 disabled:opacity-40 md:text-lg"
						disabled={isFetchingNextPage}
					>
						{isFetchingNextPage ? (
							<Loader2Icon className="h-5 w-5 animate-spin stroke-brand-primary" />
						) : (
							"Show more"
						)}
					</button>
				) : null}
			</div>
		</>
	);
};

export default PostComments;
